import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { differenceInCalendarDays, format } from "date-fns";
import { WhatsappLogo, LockKey, Users, Bed, Bathtub, ArrowsOut, Minus, Plus, Baby, Dog, CalendarBlank } from "@phosphor-icons/react";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";
import { api, waLink, formatApiErrorDetail, CONTACT_EMAIL, BOOKINGS_OPEN } from "@/lib/api";

export const BookingWidget = ({ apartment, initialRange, initialGuests = 2 }) => {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState(initialRange);
  const [guests, setGuests] = useState(Math.min(Math.max(1, initialGuests), apartment.guests));
  const [pets, setPets] = useState(0);
  const [cribs, setCribs] = useState(0);
  const [bookedDates, setBookedDates] = useState([]);
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [ratePlan, setRatePlan] = useState("flex");
  const [linen, setLinen] = useState(true);
  const [payMethod, setPayMethod] = useState("card");
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null); // {code, pct}
  const calendarRef = useRef(null);
  const scrollToCalendar = () =>
    calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const loadAvailability = () =>
    api
      .get(`/apartments/${apartment.slug}/availability`)
      .then((r) => setBookedDates(r.data.booked_dates.map((d) => new Date(`${d}T00:00:00`))))
      .catch(() => {});

  useEffect(() => {
    loadAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartment.slug]);

  const nights = useMemo(() => {
    if (range?.from && range?.to) return differenceInCalendarDays(range.to, range.from);
    return 0;
  }, [range]);

  const pct = nights > 30 ? -1 : nights >= 16 ? 55 : nights >= 8 ? 40 : nights === 7 ? 30 : 0;
  const tooLong = pct === -1;
  const nightly = useMemo(() => {
    if (!range?.from || !nights) return 0;
    let sum = 0;
    for (let i = 0; i < nights; i++) {
      const d = new Date(range.from);
      d.setDate(d.getDate() + i);
      const summer = d.getMonth() === 6 || d.getMonth() === 7;
      const weekend = d.getDay() === 5 || d.getDay() === 6;
      sum += apartment.price_per_night + (summer && weekend ? 100 : 0);
    }
    return sum;
  }, [range, nights, apartment.price_per_night]);

  const stayError = useMemo(() => {
    if (!range?.from || !nights) return null;
    const dates = [...Array(nights)].map((_, i) => {
      const d = new Date(range.from);
      d.setDate(d.getDate() + i);
      return d;
    });
    if (dates.some((d) => d.getMonth() !== 6 && d.getMonth() !== 7)) return t("booking.errOpenMonths");
    if (dates.some((d) => d.getMonth() === 6 || d.getMonth() === 7) && nights < 4) return t("booking.errSummer", { nights });
    const hasFri = dates.some((d) => d.getDay() === 5);
    const hasSat = dates.some((d) => d.getDay() === 6);
    if (hasFri !== hasSat) return t("booking.errWeekend");
    return null;
  }, [range, nights, t]);
  const discount = pct > 0 ? (nightly * pct) / 100 : 0;
  const petsFee = pets * 60;
  const linenFee = linen ? 20 : 0;
  const accommodation = nightly - discount;
  const stdDiscount = accommodation * 0.15;
  const nonrefDiscount = ratePlan === "nonref" ? stdDiscount : 0;
  const codeDiscount = appliedCode ? (accommodation * appliedCode.pct) / 100 : 0;
  const totalFlex = tooLong ? 0 : accommodation - codeDiscount + 70 + linenFee + petsFee;
  const totalStd = tooLong ? 0 : accommodation - stdDiscount - codeDiscount + 70 + linenFee + petsFee;
  const total = ratePlan === "nonref" ? totalStd : totalFlex;

  // Gastos de gestión del pago: 1% igual para todos los métodos (mismo cálculo que el servidor)
  const PAY_FEES = {
    card: { pct: 1, fixed: 0 },
    paypal: { pct: 1, fixed: 0 },
    bizum: { pct: 1, fixed: 0 },
  };
  const payFee = Math.round((total * PAY_FEES[payMethod].pct) / 100 * 100) / 100 + PAY_FEES[payMethod].fixed;
  const totalToPay = Math.round((total + payFee) * 100) / 100;
  const money = (v) =>
    new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(v);

  const hasBookedInside = () => {
    if (!range?.from || !range?.to) return false;
    return bookedDates.some((d) => d >= range.from && d < range.to);
  };

  const [alternatives, setAlternatives] = useState(null);
  const [altDates, setAltDates] = useState([]);
  useEffect(() => {
    if (!range?.from || !range?.to || !hasBookedInside()) {
      setAlternatives(null);
      setAltDates([]);
      return;
    }
    api
      .get("/availability/search", {
        params: { checkin: format(range.from, "yyyy-MM-dd"), checkout: format(range.to, "yyyy-MM-dd"), guests },
      })
      .then((r) => {
        setAlternatives(r.data.results.filter((x) => x.available && x.apartment.slug !== apartment.slug));
        const self = r.data.results.find((x) => x.apartment.slug === apartment.slug);
        setAltDates(self?.alt_dates || []);
      })
      .catch(() => { setAlternatives([]); setAltDates([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, guests, bookedDates]);

  const submit = async () => {
    if (!BOOKINGS_OPEN) return toast.error(t("booking.closed", { email: CONTACT_EMAIL }));
    if (!range?.from || !range?.to || nights < 1) return toast.error(t("booking.errDates"));
    if (tooLong) return toast.error(t("booking.consult"));
    if (stayError) return toast.error(stayError);
    if (hasBookedInside()) return toast.error(t("booking.errConflict"));
    if (!guest.name.trim() || !guest.email.trim() || !guest.phone.trim())
      return toast.error(t("booking.errFields"));
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        apartment_slug: apartment.slug,
        checkin: format(range.from, "yyyy-MM-dd"),
        checkout: format(range.to, "yyyy-MM-dd"),
        guest_name: guest.name,
        guest_email: guest.email,
        guest_phone: guest.phone,
        guests,
        pets,
        cribs,
        linen,
        rate_plan: ratePlan,
        pay_method: payMethod,
        discount_code: appliedCode?.code || null,
        lang: i18n.language,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url || data.paypal_url;
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 409 ? t("booking.errConflict") : formatApiErrorDetail(err.response?.data?.detail) || t("booking.errGeneric"));
      if (status === 409) loadAvailability();
      setLoading(false);
    }
  };

  const waText = t("whatsapp.booking", {
    apartment: apartment.name,
    checkin: range?.from ? format(range.from, "yyyy-MM-dd") : "…",
    checkout: range?.to ? format(range.to, "yyyy-MM-dd") : "…",
  });

  return (
    <div data-testid="booking-widget" className="border border-line bg-white">
      <div className="p-6 md:p-8 border-b border-line">
        <div className="flex items-baseline justify-between">
          <p>
            <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45">{t("detail.from")} </span>
            <span className="font-serif text-4xl text-ink">{money(apartment.price_per_night)}</span>
            <span className="text-sm text-ink/50"> {t("detail.perNight")}</span>
          </p>
          <span className="w-3 h-3 rounded-full" style={{ background: apartment.color }} />
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-xs uppercase tracking-[0.12em] text-ink/55">
          <span className="flex items-center gap-1.5"><Users size={14} /> {apartment.guests} {t("detail.guests")}</span>
          <span className="flex items-center gap-1.5"><Bed size={14} /> {apartment.bedrooms} {t("detail.bedrooms")}</span>
          <span className="flex items-center gap-1.5"><Bathtub size={14} /> {apartment.bathrooms} {t("detail.baths")}</span>
          <span className="flex items-center gap-1.5"><ArrowsOut size={14} /> {apartment.area} {t("detail.area")}</span>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-4">{t("booking.title")}</p>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <button type="button" data-testid="checkin-display" onClick={scrollToCalendar} className="border border-line p-3 text-left cursor-pointer hover:border-terracotta transition-colors">
            <p className="text-[10px] uppercase tracking-[0.2em] text-ink/45 flex items-center gap-1.5"><CalendarBlank size={12} /> {t("booking.checkin")}</p>
            <p className="mt-1 text-ink">{range?.from ? format(range.from, "dd/MM/yyyy") : "—"}</p>
          </button>
          <button type="button" data-testid="checkout-display" onClick={scrollToCalendar} className="border border-line p-3 text-left cursor-pointer hover:border-terracotta transition-colors">
            <p className="text-[10px] uppercase tracking-[0.2em] text-ink/45 flex items-center gap-1.5"><CalendarBlank size={12} /> {t("booking.checkout")}</p>
            <p className="mt-1 text-ink">{range?.to ? format(range.to, "dd/MM/yyyy") : "—"}</p>
          </button>
        </div>
        {nights > 0 && (
          <p data-testid="nights-display" className="mt-2 text-[11px] uppercase tracking-[0.15em] text-terracotta">
            {nights} {t("booking.nights")}
          </p>
        )}

        <div ref={calendarRef} className="mt-5 scroll-mt-28">
          <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-4">{t("booking.datesTitle")}</p>
          <AvailabilityCalendar bookedDates={bookedDates} range={range} onSelect={setRange} closed={!BOOKINGS_OPEN} />
          {!BOOKINGS_OPEN ? (
            <p data-testid="bookings-closed-note" className="mt-3 border border-terracotta/40 bg-terracotta/5 px-4 py-3 text-xs leading-relaxed text-terracotta">
              {t("booking.closed", { email: CONTACT_EMAIL })}
            </p>
          ) : (
            <p data-testid="open-months-note" className="mt-3 text-[11px] leading-relaxed text-ink/50 italic">
              {t("booking.openNote")}
            </p>
          )}
          <div className="flex items-center gap-5 mt-3 text-[10px] uppercase tracking-[0.15em] text-ink/50">
            <span className="flex items-center gap-2"><span className="w-3 h-3 border border-line bg-white inline-block" />{t("booking.legendFree")}</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 inline-block" style={{ backgroundImage: "repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(200,90,50,.35) 3px, rgba(200,90,50,.35) 4px)" }} />{t("booking.legendBusy")}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { label: t("detail.guests"), value: guests, set: setGuests, min: 1, max: apartment.guests, tid: "guests" },
            { label: t("booking.dogs"), value: pets, set: setPets, min: 0, max: 3, tid: "pets" },
            { label: t("booking.cribs"), value: cribs, set: setCribs, min: 0, max: 2, tid: "cribs" },
          ].map((s) => (
            <div key={s.tid} className="border border-line p-3 flex flex-col gap-2">
              <span className="text-[10px] uppercase tracking-[0.12em] text-ink/45">{s.label}</span>
              <span className="flex items-center justify-between">
                <button type="button" data-testid={`${s.tid}-minus`} onClick={() => s.set(Math.max(s.min, s.value - 1))} className="w-6 h-6 border border-line flex items-center justify-center text-ink hover:border-terracotta hover:text-terracotta transition-colors" aria-label="-">
                  <Minus size={11} />
                </button>
                <span data-testid={`${s.tid}-count`} className="text-sm text-ink">{s.value}</span>
                <button type="button" data-testid={`${s.tid}-plus`} onClick={() => s.set(Math.min(s.max, s.value + 1))} className="w-6 h-6 border border-line flex items-center justify-center text-ink hover:border-terracotta hover:text-terracotta transition-colors" aria-label="+">
                  <Plus size={11} />
                </button>
              </span>
            </div>
          ))}
        </div>

        <p data-testid="extras-note" className="mt-4 flex items-center gap-2 text-[11px] leading-relaxed text-[#4A6B53]">
          <Baby size={14} className="shrink-0" />
          <span>{t("booking.extrasNote")}</span>
          <Dog size={14} className="shrink-0" />
        </p>

        {stayError && (
          <p data-testid="stay-error" className="mt-4 border border-terracotta/40 bg-terracotta/5 px-4 py-3 text-xs leading-relaxed text-terracotta">
            {stayError}
          </p>
        )}

        {alternatives && (
          <div data-testid="alt-apartments" className="mt-4 border border-atlantic/30 bg-atlantic/5 px-4 py-3">
            {altDates.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-ink/75">{t("booking.altDatesTitle")}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {altDates.map((d) => (
                    <button
                      key={d.checkin}
                      type="button"
                      data-testid={`alt-date-${d.checkin}`}
                      onClick={() => {
                        const [y1, m1, d1] = d.checkin.split("-").map(Number);
                        const [y2, m2, d2] = d.checkout.split("-").map(Number);
                        setRange({ from: new Date(y1, m1 - 1, d1), to: new Date(y2, m2 - 1, d2) });
                      }}
                      className="text-[11px] border border-terracotta/50 text-terracotta px-3 py-1.5 hover:bg-terracotta hover:text-sand transition-colors"
                    >
                      {d.checkin} → {d.checkout}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs font-medium text-ink/75">{t("booking.altTitle")}</p>
            {alternatives.length > 0 ? (
              <ul className="mt-2 space-y-1.5">
                {alternatives.map((a) => (
                  <li key={a.apartment.slug}>
                    <Link
                      data-testid={`alt-${a.apartment.slug}`}
                      to={`/apartamento/${a.apartment.slug}?checkin=${format(range.from, "yyyy-MM-dd")}&checkout=${format(range.to, "yyyy-MM-dd")}&guests=${guests}`}
                      className="text-xs text-atlantic underline underline-offset-2 hover:text-terracotta transition-colors"
                    >
                      {a.apartment.name} · {money(a.total)}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-[11px] text-ink/50">{t("booking.altNone")}</p>
            )}
          </div>
        )}

        {nights > 0 && !tooLong && (
          <div data-testid="rate-selector" className="mt-6">
            <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3">{t("booking.rateTitle")}</p>
            <div className="flex flex-col gap-3">
              {[
                { id: "flex", label: t("booking.rateFlex"), price: totalFlex, policy: t("booking.rateFlexPolicy") },
                { id: "nonref", label: t("booking.rateStd"), price: totalStd, policy: t("booking.rateStdPolicy") },
              ].map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`rate-plan-${r.id}`}
                  onClick={() => setRatePlan(r.id)}
                  className={`border p-4 text-left transition-colors duration-300 ${ratePlan === r.id ? "border-terracotta bg-terracotta/5" : "border-line hover:border-ink/40"}`}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2.5">
                      <span className={`w-3 h-3 rounded-full border transition-colors ${ratePlan === r.id ? "border-terracotta bg-terracotta" : "border-ink/30"}`} />
                      <span className="text-xs uppercase tracking-[0.15em] text-ink">{r.label}</span>
                    </span>
                    <span className="font-serif text-xl text-ink">{money(r.price)}</span>
                  </span>
                  <span className="block mt-2.5 text-[11px] leading-relaxed text-ink/55">{r.policy}</span>
                </button>
              ))}
            </div>
            <p data-testid="deposit-note" className="mt-3 text-[11px] leading-relaxed text-ink/55 border border-line bg-sand/60 px-3 py-2">
              {t("booking.depositNote", { amount: money(Math.round(total * 0.1 * 100) / 100) })}
            </p>
          </div>
        )}

        {nights > 0 && !tooLong && (
          <div data-testid="price-summary" className="mt-5 py-4 border-y border-line text-sm">
            <div className="flex justify-between text-ink/70">
              <span>{nights} {t("booking.nights")}</span>
              <span>{money(nightly)}</span>
            </div>
            {pct > 0 && (
              <div className="flex justify-between text-[#4A6B53] mt-1.5">
                <span>{t("booking.discountLine", { pct })}</span>
                <span>−{money(discount)}</span>
              </div>
            )}
            {ratePlan === "nonref" && (
              <div data-testid="nonref-line" className="flex justify-between text-[#4A6B53] mt-1.5">
                <span>{t("booking.nonrefLine")}</span>
                <span>−{money(nonrefDiscount)}</span>
              </div>
            )}
            {appliedCode && (
              <div data-testid="code-line" className="flex justify-between text-[#4A6B53] mt-1.5">
                <span>{t("booking.codeLine", { code: appliedCode.code, pct: appliedCode.pct })}</span>
                <span>−{money(codeDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between text-ink/70 mt-1.5">
              <span>{t("booking.cleaning")}</span>
              <span>{money(70)}</span>
            </div>
            <label data-testid="linen-toggle" className="flex items-center justify-between text-ink/70 mt-1.5 cursor-pointer select-none">
              <span className="flex items-center gap-2">
                <input
                  data-testid="linen-checkbox"
                  type="checkbox"
                  checked={linen}
                  onChange={(e) => setLinen(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#C85A32]"
                />
                {t("booking.linen")} <span className="text-ink/40 text-xs">({t("booking.linenOptional")})</span>
              </span>
              <span>{linen ? money(20) : money(0)}</span>
            </label>
            {pets > 0 && (
              <div className="flex justify-between text-ink/70 mt-1.5">
                <span>{t("booking.petsFee")} ({pets} × {money(60)})</span>
                <span>{money(petsFee)}</span>
              </div>
            )}
            <div data-testid="pay-fee-line" className="flex justify-between text-ink/70 mt-1.5">
              <span>{t("booking.feeService")}</span>
              <span>+{money(payFee)}</span>
            </div>
            <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-line">
              <span className="text-ink/60">{t("booking.total")}</span>
              <span data-testid="price-total" className="font-serif text-3xl text-ink">{money(totalToPay)}</span>
            </div>
          </div>
        )}

        {nights > 0 && !tooLong && (
          <div data-testid="code-box" className="mt-4">
            {appliedCode ? (
              <p className="flex items-center justify-between border border-[#4A6B53]/40 bg-[#4A6B53]/5 px-3 py-2.5 text-xs text-[#4A6B53]">
                <span>{t("booking.codeApplied", { code: appliedCode.code, pct: appliedCode.pct })}</span>
                <button type="button" data-testid="code-remove" onClick={() => { setAppliedCode(null); setCodeInput(""); }} className="uppercase tracking-[0.1em] hover:underline">{t("booking.codeRemove")}</button>
              </p>
            ) : (
              <div className="flex gap-2">
                <input
                  data-testid="code-input"
                  className="input-line flex-1 !py-2.5 text-xs uppercase tracking-[0.1em]"
                  placeholder={t("booking.codePlaceholder")}
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                />
                <button
                  type="button"
                  data-testid="code-apply"
                  onClick={async () => {
                    if (!codeInput.trim()) return;
                    try {
                      const { data } = await api.post("/codes/validate", { code: codeInput.trim() });
                      setAppliedCode(data);
                      toast.success(t("booking.codeOk", { pct: data.pct }));
                    } catch {
                      toast.error(t("booking.codeInvalid"));
                    }
                  }}
                  className="px-4 py-2.5 border border-ink/25 text-ink text-[10px] uppercase tracking-[0.15em] hover:border-terracotta hover:text-terracotta transition-colors"
                >
                  {t("booking.codeApply")}
                </button>
              </div>
            )}
          </div>
        )}

        {nights > 0 && !tooLong && (
          <div data-testid="pay-selector" className="mt-6">
            <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3">{t("booking.payTitle")}</p>
            <div className="grid grid-cols-2 gap-3">
              {["card", "paypal", "bizum"].map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`pay-method-${m}`}
                  onClick={() => setPayMethod(m)}
                  className={`border p-3.5 text-left transition-colors duration-300 ${payMethod === m ? "border-terracotta bg-terracotta/5" : "border-line hover:border-ink/40"}`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full border transition-colors ${payMethod === m ? "border-terracotta bg-terracotta" : "border-ink/30"}`} />
                    <span className="text-xs uppercase tracking-[0.12em] text-ink">{t(`booking.pay_${m}`)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tooLong && (
          <div data-testid="consult-long-stay" className="mt-5 py-4 border-y border-line">
            <p className="text-sm text-ink/70">{t("booking.consult")}</p>
          </div>
        )}

        <div className="flex flex-col gap-5 mt-6">
          <input data-testid="guest-name" className="input-line" placeholder={t("booking.name")} value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input data-testid="guest-email" type="email" className="input-line" placeholder={t("booking.email")} value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} />
            <input data-testid="guest-phone" className="input-line" placeholder={t("booking.phone")} value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} />
          </div>
        </div>

        <button
          data-testid="book-submit-btn"
          onClick={submit}
          disabled={loading || tooLong}
          className="w-full mt-7 py-4 bg-terracotta text-sand text-xs uppercase tracking-[0.2em] hover:bg-atlantic transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? t("booking.loading") : t("booking.cta")}
        </button>
        <a
          data-testid="book-whatsapp-btn"
          href={waLink(waText)}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full mt-3 py-4 border border-ink/20 text-ink text-xs uppercase tracking-[0.2em] hover:border-[#25D366] hover:text-[#1da851] transition-colors duration-300 flex items-center justify-center gap-2"
        >
          <WhatsappLogo size={16} />
          {t("booking.whatsapp")}
        </a>
        {nights > 0 && (
        <div data-testid="rules-box" className="mt-5 border border-line p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-ink/60">{t("rules.title")}</p>
          <ul className="mt-3 space-y-1.5 text-[11px] leading-relaxed text-ink/60">
            <li>· {t("rules.r1")}</li>
            <li>· {t("rules.r2")}</li>
            <li>· {t("rules.r3")}</li>
            <li>· {t("rules.r4")}</li>
            <li>· {t("rules.r5")}</li>
          </ul>
        </div>
        )}
        <p className="flex items-center justify-center gap-2 mt-4 text-[10px] uppercase tracking-[0.15em] text-ink/40">
          <LockKey size={12} /> {t("booking.secure")}
        </p>
      </div>
    </div>
  );
};
